import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { book, colors } from '@/styles';
import { RadioButton, ToggleSwitch } from '@/atoms';

/**
 * Checkbox component
 *
 * @version 1.0.0
 * @param {import('./Checkbox').CheckboxProps} props
 */
function Checkbox({
  checked,
  className,
  defaultChecked,
  disabled,
  name,
  id,
  onChange,
  title,
  type,
  isRebrand,
  isInline,
  ...extra
}) {
  const [state, setState] = useState(defaultChecked);

  useEffect(() => {
    if (checked !== null) {
      setState(checked);
    }
  }, [checked]);

  function handleChange() {
    if (checked === null) {
      setState(!state);
    }
    onChange(!state);
  }

  const classes = classnames(className, {
    'not-native': type !== 'native',
    inline: isInline,
    rebrand: isRebrand,
  });

  return (
    <div className={classes}>
      {type !== 'native' && type !== 'toggle' && (
        <RadioButton
          title={title}
          role="checkbox"
          onChange={handleChange}
          selected={state}
          disabled={disabled}
          selectedColor={isRebrand ? colors.electricBlue : colors.silverSandAlt}
          {...extra}
        />
      )}
      {type === 'toggle' && (
        <ToggleSwitch
          title={title}
          role="checkbox"
          onClick={handleChange}
          isActive={state}
          disabled={disabled}
          {...extra}
        />
      )}
      <input
        type="checkbox"
        name={name}
        id={id ?? name}
        disabled={disabled}
        aria-hidden={type !== 'native'}
        defaultChecked={state}
        onChange={handleChange}
      />
    </div>
  );
}

Checkbox.propTypes = {
  /** Emotion base style */
  className: PropTypes.string.isRequired,
  /** Name */
  name: PropTypes.string.isRequired,
  /** ID */
  id: PropTypes.string,
  /** onChange handler */
  onChange: PropTypes.func,
  /** Type of the checkbox */
  type: PropTypes.oneOf(['native', 'toggle', 'circle', 'heart', 'square', 'star']),
  /** The default value of the checkbox */
  defaultChecked: PropTypes.bool,
  /** If checked is set, the component is set to external state control */
  checked: PropTypes.bool,
  /** Should the checkbox be disabled? */
  disabled: PropTypes.bool,
  /** Title for checkbox */
  title: PropTypes.string,
  /** Should the checkbox have the rebrand colors and style */
  isRebrand: PropTypes.bool,
  /** Should the checkbox be displayed inline */
  isInline: PropTypes.bool,
};

Checkbox.defaultProps = {
  id: null,
  disabled: false,
  defaultChecked: false,
  checked: null,
  onChange: (value) => {},
  type: 'native',
  title: '',
  isRebrand: false,
  isInline: false,
};

const styledCheckbox = styled(Checkbox)`
  ${book()}
  &.not-native input {
    display: none;
  }

  &.rebrand > input[type='checkbox'] {
    accent-color: ${colors.electricBlue};
    margin-left: 0;
    width: 25px;
    height: 25px;
  }
`;

export default styledCheckbox;
